import { NOTIFICATION_DIALOG_ACTIONS } from '@src/app/notifications/dialog';
import { useNotifilcationDialog } from '@src/app/notifications/dialog/hooks';
import { useFormContext } from '@src/shared/form';
import {
  formatDateRange,
  formatIsoDate,
  getServerErrorMessage,
} from '@src/shared/lib';
import { Button, SubmitButton } from '@src/shared/ui/Button';
import { Form, FormControlLabel } from '@src/shared/ui/form';
import { Switch } from '@src/shared/ui/inputs';
import { Stack } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';
import { createSchema, Yup } from '@src/shared/validation';
import { useEffect, useMemo, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import {
  getAbsenceBlock,
  getEditedEntry,
  hasRangeChanged,
  isoDaysBetween,
  isoRuns,
  type LoadedRange,
  localDateFromIso,
  requiresChangeRequest,
} from '../../lib';
import { useTimeline } from '../../model/hooks';
import { useAbsenceTypes } from '../../model/query';
import useAbsenceEntry from '../../model/query/useAbsenceEntry';
import { getLoadedRange } from '../../model/query/useAbsences';
import type { Absence, AbsenceType } from '../../types';
import {
  ABSENCE_LOCK_STATUS,
  isAbsenceLockErrorStatus,
  isAbsenceLockHeldStatus,
  type SlotData,
  type TimelineSlot,
} from '../../types';
import FormFieldset from './Fields';
import FormHeader from './FormHeader';

type OverrideAbsenceDetail = {
  key: string;
  typeName: string;
  from: Date;
  to: Date;
  startTime: string | null;
  endTime: string | null;
};

function getOverrideAbsenceDetails(
  selectedIds: number[],
  absences: Absence[],
  absenceTypes: AbsenceType[],
): OverrideAbsenceDetail[] {
  const selectedAbsences = absences.filter((absence) =>
    selectedIds.includes(absence.id),
  );

  const groupedAbsences = new Map<string, OverrideAbsenceDetail>();
  const hourlyAbsences: OverrideAbsenceDetail[] = [];

  selectedAbsences.forEach((absence) => {
    const typeName =
      absence.typeId != null
        ? absenceTypes.find((type) => type.id === absence.typeId)?.name
        : undefined;
    const resolvedTypeName = typeName ?? 'Unknown';
    const isHourly = !!absence.startTime && !!absence.endTime;

    if (isHourly) {
      hourlyAbsences.push({
        key: `absence-${absence.id}`,
        typeName: resolvedTypeName,
        from: absence.date,
        to: absence.date,
        startTime: absence.startTime,
        endTime: absence.endTime,
      });
      return;
    }

    const groupKey =
      absence.typeId != null
        ? `type-${absence.typeId}`
        : `absence-${absence.id}`;
    const existingGroup = groupedAbsences.get(groupKey);

    if (!existingGroup) {
      groupedAbsences.set(groupKey, {
        key: groupKey,
        typeName: resolvedTypeName,
        from: absence.date,
        to: absence.date,
        startTime: null,
        endTime: null,
      });
      return;
    }

    if (absence.date < existingGroup.from) {
      existingGroup.from = absence.date;
    }

    if (absence.date > existingGroup.to) {
      existingGroup.to = absence.date;
    }
  });

  return [...groupedAbsences.values(), ...hourlyAbsences].sort(
    (a, b) => a.from.getTime() - b.from.getTime(),
  );
}

export type WithdrawScope = {
  /** Granularity of the absence being withdrawn; fixed for the whole flow. */
  hourly: boolean;
  minDate: Date;
  maxDate: Date;
  minTime: string | null;
  maxTime: string | null;
};

export type ScheduleFormValues = {
  userId: number | null;
  dateFrom: Date | null;
  dateTo?: Date | null;
  startTime?: string | null;
  endTime?: string | null;
  typeId: number | null;
  tentative?: boolean;
  notes?: string;
  notesHr?: string;
};

function getDefaultValues(slotData: SlotData | null): ScheduleFormValues {
  const values: ScheduleFormValues = {
    userId: null,
    dateFrom: null,
    dateTo: null,
    startTime: null,
    endTime: null,
    typeId: null,
    tentative: false,
    notes: '',
    notesHr: '',
  };
  if (!slotData) return values;

  const userId = slotData.userId;
  if (userId) values.userId = userId;

  const dateFrom = slotData.start;
  if (dateFrom) values.dateFrom = new Date(dateFrom);

  const dateTo = slotData.end;
  if (dateTo) values.dateTo = new Date(dateTo);

  values.startTime = slotData.startTime ?? null;
  values.endTime = slotData.endTime ?? null;
  values.typeId = slotData.typeId;
  values.tentative = slotData.tentative;
  values.notes = slotData.notes;
  values.notesHr = slotData.notesHr;

  return values;
}

/**
 * Bounds for withdrawal: an absence can only be given back within the range it
 * already occupies, and its granularity is fixed — an all-day absence cannot be
 * turned into an hourly one on the way out. Returns null outside withdraw mode.
 *
 * The bounds span the whole contiguous block the selection belongs to, not just
 * the days that happened to be selected, so someone who clicked a single day of
 * a multi-day absence can still widen the withdrawal to its neighbours.
 */
function useWithdrawScope(
  active: boolean,
  slot: TimelineSlot | null,
  absences: Absence[],
): WithdrawScope | null {
  return useMemo(() => {
    if (!active) return null;

    const ids =
      slot?.editedAbsenceIds ??
      (slot?.editedAbsenceId ? [slot.editedAbsenceId] : []);
    const selected = absences
      .filter((absence) => ids.includes(absence.id))
      .sort((left, right) => left.dateStr.localeCompare(right.dateStr));

    if (!selected.length) return null;

    const anchor = selected[0];
    // Hourly absences are never part of an all-day block, so they stay bound to
    // themselves and a single-day block collapses to the same thing.
    const block = anchor.allDay ? getAbsenceBlock(anchor, absences) : [];
    const range = block.length ? block : selected;
    const first = range[0];
    const last = range[range.length - 1];

    return {
      hourly: !anchor.allDay,
      minDate: new Date(`${first.dateStr}T00:00:00`),
      maxDate: new Date(`${last.dateStr}T00:00:00`),
      minTime: anchor.startTime,
      maxTime: anchor.endTime,
    };
  }, [active, slot, absences]);
}

function TentativeSwitch({ disabled }: { disabled: boolean }) {
  const { control } = useFormContext() as UseFormReturn<ScheduleFormValues>;
  return (
    <Controller
      name="tentative"
      control={control}
      render={({ field }) => (
        <Switch
          disabled={disabled}
          checked={Boolean(field.value)}
          onBlur={field.onBlur}
          onChange={(_, checked) => field.onChange(checked)}
          slotProps={{ input: { ref: field.ref } }}
        />
      )}
    />
  );
}

function ScheduleFormActions({
  disabled,
  withdrawing,
}: {
  disabled: boolean;
  withdrawing: boolean;
}) {
  const { absenceTypes } = useAbsenceTypes();
  const { selectedSlot, withdraw } = useTimeline();
  const { setValue, watch } =
    useFormContext() as UseFormReturn<ScheduleFormValues>;
  const [typeId, tentative] = watch(['typeId', 'tentative']);
  const selectedType = absenceTypes.find((type) => type.id === typeId);
  const canSaveTentative = !!selectedType?.tentativeable && !withdrawing;
  const [isWithdrawPending, setIsWithdrawPending] = useState(false);

  useEffect(() => {
    if (!canSaveTentative && tentative) {
      setValue('tentative', false);
    }
  }, [canSaveTentative, setValue, tentative]);

  if (withdrawing) {
    // Whatever the narrowed range currently covers, as kept up to date by the
    // fieldset's round-trip effect.
    const withdrawIds = selectedSlot?.selectedIds ?? [];

    const onWithdraw = async () => {
      if (!withdrawIds.length || isWithdrawPending) {
        return;
      }
      setIsWithdrawPending(true);
      try {
        await withdraw(withdrawIds);
      } finally {
        setIsWithdrawPending(false);
      }
    };

    return (
      <Stack spacing={1.25}>
        <Button
          disabled={disabled || !withdrawIds.length || isWithdrawPending}
          variant="contained"
          type="button"
          onClick={onWithdraw}
        >
          <Typography variant="body1">Withdraw Absence</Typography>
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.25}>
      {canSaveTentative && (
        <FormControlLabel
          control={<TentativeSwitch disabled={disabled} />}
          label={<Typography variant="body2">Tentative</Typography>}
        />
      )}
      <SubmitButton
        disabled={disabled}
        variant="contained"
        loadingPosition="start"
      >
        <Typography variant="body1">Request</Typography>
      </SubmitButton>
    </Stack>
  );
}

export default function ScheduleForm() {
  const {
    absenceLock,
    absenceEditor,
    selectedSlot,
    selectedSlotData,
    book,
    isDayView,
    absences,
    sidebarMode,
    visibleRange,
  } = useTimeline();
  const { absenceTypes } = useAbsenceTypes();
  const loadAbsenceEntry = useAbsenceEntry();
  const withdrawScope = useWithdrawScope(
    sidebarMode === 'withdraw',
    selectedSlot,
    absences,
  );

  const schema = useMemo(
    () =>
      createSchema({
        userId: Yup.number().required('This field is required'),
        dateFrom: Yup.date().required('This field is required'),
        dateTo: isDayView
          ? Yup.date().nullable()
          : Yup.date().required('This field is required'),
        typeId: Yup.number().required('This field is required'),
        ...(isDayView
          ? {
              startTime: Yup.string().required('This field is required'),
              endTime: Yup.string().required('This field is required'),
            }
          : {}),
      }),
    [isDayView],
  );
  const alert = useNotifilcationDialog();
  const [serverError, setServerError] = useState<string | null>(null);

  const lockMessage = useMemo(() => {
    if (!isAbsenceLockErrorStatus(absenceLock.status)) {
      return null;
    }
    // Waiting matters more here than in the details pane -- there is unsaved
    // work in the fields -- so say who has it, live, rather than repeating
    // whatever the failed acquire happened to return.
    if (absenceEditor) {
      return `${absenceEditor.ownerName} is editing this employee right now.`;
    }
    return absenceLock.message || 'This user is currently locked for editing.';
  }, [absenceEditor, absenceLock.message, absenceLock.status]);

  const isFormEditable = isAbsenceLockHeldStatus(absenceLock.status);

  useEffect(() => {
    if (isAbsenceLockHeldStatus(absenceLock.status) || !selectedSlot?.userId) {
      setServerError(null);
    }
  }, [absenceLock.status, selectedSlot?.userId]);

  const onSubmit = async (values: Record<string, unknown>) => {
    setServerError(null);
    // What the edit pane says, and failing that what the selection says. A
    // drag carries dates and no absence, so an entry it plainly reshapes has
    // to be recognised here or the submit reads as a booking over the top --
    // which is the same form, and a different question for the approver.
    let edited = getEditedEntry(selectedSlot, absences, values.typeId, {
      infer: !isDayView,
      loaded: visibleRange ? getLoadedRange(visibleRange) : null,
    });
    // Either the entry runs off the calendar, or the pane named one whose
    // rows have since left it. Both mean the same thing: only part of what
    // is being changed is in hand. Fetched before anything is decided, so
    // the days it loses are the entry's own rather than the ones that happen
    // to be on screen, and so the confirmation below can name them.
    if (edited.clipped || edited.needsRows) {
      const anchor = edited.clipped?.dateStr ?? selectedSlot?.start ?? null;
      // Enough is whatever settles the question: the rows behind the ids the
      // pane named, or the whole of the entry a selection reshapes. Asking
      // the resolver itself covers both without naming which case this is.
      const isEnough = (rows: Absence[], range: LoadedRange) => {
        const next = getEditedEntry(selectedSlot, rows, values.typeId, {
          infer: true,
          loaded: range,
        });
        return next.ids.length > 0 && !next.clipped && !next.needsRows;
      };

      const loaded =
        selectedSlot?.userId && anchor
          ? await loadAbsenceEntry(selectedSlot.userId, anchor, isEnough)
          : null;
      const resolved = loaded
        ? getEditedEntry(selectedSlot, loaded.absences, values.typeId, {
            infer: true,
            loaded: loaded.range,
          })
        : null;
      // Nothing may be sent while only part of the entry is in hand.
      // Submitting would read as a fresh booking over the days on screen and
      // leave the rest of it where it was -- the very thing the fetch is
      // here to prevent -- and the confirmation would have named only what
      // it could see. The user is told instead, and nothing has happened
      // yet, so pressing Request again is all it takes.
      if (!resolved || resolved.clipped || resolved.needsRows) {
        setServerError(
          'Could not load the whole absence being changed. Please try again.',
        );
        return undefined;
      }
      edited = resolved;
    }
    const { ids: editedAbsenceIds, absences: editedAbsences } = edited;
    const editedAbsence = editedAbsences[0] ?? null;
    const isTypeChanged =
      !!editedAbsence && editedAbsence.typeId !== values.typeId;

    // An hourly request rewrites a single row: the one sitting on the day the
    // hours land on. A multi-day block leaves `editedAbsenceId` null -- it
    // names no single row -- and without an id the server reads the request as
    // a fresh booking and turns it away, because the all-day row is still on
    // that day. Naming the row the hours belong to is what tells the two
    // apart, and is what the single-day edit has been doing all along.
    const hourlyDate =
      values.startTime && values.dateFrom
        ? formatIsoDate(values.dateFrom as Date)
        : null;
    const hourlyTarget = hourlyDate
      ? (editedAbsences.find((row) => row.dateStr === hourlyDate) ?? null)
      : null;

    const payload = {
      ...values,
      absenceId: selectedSlot?.editedAbsenceId ?? hourlyTarget?.id ?? null,
      editedAbsenceIds,
      userId: selectedSlot?.userId,
      dateFrom: values.dateFrom,
      dateTo: !values.dateTo ? values.dateFrom : values.dateTo,
    };

    // One change at a time. Moving an absence and retyping it in one go would
    // be two different questions for the approver, and the request can only
    // describe one of them.
    if (
      isTypeChanged &&
      hasRangeChanged(
        editedAbsences,
        payload.dateFrom as Date,
        payload.dateTo as Date,
      )
    ) {
      setServerError('Change either the dates or the type, not both at once.');
      return undefined;
    }

    // Making a day somebody *approved* tentative gives that approval up, so
    // the server treats it as a change to ask about rather than a date change
    // -- this dialog must not promise a removal that will not happen. A day
    // nobody approved is the user's either way, and still reshapes.
    const isMadeTentative =
      !!values.tentative &&
      editedAbsences.some(
        (absence) =>
          !absence.absenceTentative &&
          requiresChangeRequest(
            absence,
            absenceTypes.find((type) => type.id === absence.typeId),
          ),
      );
    // A same-type edit with different dates is a date change: the days the
    // edit drops are withdrawn and the new days are requested, neither of
    // which is visible in the form -- so spell out exactly what will happen
    // before sending it.
    if (
      !isDayView &&
      editedAbsences.length > 0 &&
      !isTypeChanged &&
      !isMadeTentative &&
      hasRangeChanged(
        editedAbsences,
        payload.dateFrom as Date,
        payload.dateTo as Date,
      )
    ) {
      const requested = isoDaysBetween(
        payload.dateFrom as Date,
        payload.dateTo as Date,
      );
      const covered = editedAbsences.map((absence) => absence.dateStr);
      const removed = covered.filter((day) => !requested.includes(day));
      const kept = covered.filter((day) => requested.includes(day));
      const added = requested.filter((day) => !covered.includes(day));

      // Days the new range rolls over that hold something else entirely are
      // replaced outright, exactly as they would be by a plain booking, and
      // that warning must not be lost to the date-change dialog.
      const overriddenIds = absences
        .filter(
          (absence) =>
            absence.userId === selectedSlot?.userId &&
            added.includes(absence.dateStr) &&
            !editedAbsenceIds.includes(absence.id) &&
            !requiresChangeRequest(
              absence,
              absenceTypes.find((type) => type.id === absence.typeId),
            ),
        )
        .map((absence) => absence.id);
      const overrideDetails = getOverrideAbsenceDetails(
        overriddenIds,
        absences,
        absenceTypes,
      );

      const { action } = await alert.open({
        title: 'Warning',
        children: (
          <Stack spacing={1} alignItems="center">
            <Typography variant="body1">
              You are about to change the dates of this absence:
            </Typography>
            <table>
              <tbody>
                {(
                  [
                    ['Remove', removed],
                    ['Keep', kept],
                    ['Add', added],
                  ] as const
                )
                  .filter(([, days]) => days.length > 0)
                  .map(([label, days]) => (
                    <tr key={label}>
                      <td style={{ textAlign: 'left', paddingRight: 16 }}>
                        <Typography variant="h4">{label}:</Typography>
                      </td>
                      <td style={{ textAlign: 'left' }}>
                        <Typography variant="h4">
                          {isoRuns(days)
                            .map(([from, to]) =>
                              formatDateRange(
                                localDateFromIso(from),
                                localDateFromIso(to),
                              ),
                            )
                            .join(', ')}
                        </Typography>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {overrideDetails.length > 0 && (
              <>
                <Typography variant="body1">
                  This will also override existing absence events:
                </Typography>
                <table>
                  <tbody>
                    {overrideDetails.map(
                      ({ key, typeName, from, to, startTime, endTime }) => (
                        <tr key={key}>
                          <td style={{ textAlign: 'left', paddingRight: 16 }}>
                            <Typography variant="h4">{typeName}:</Typography>
                          </td>
                          <td style={{ textAlign: 'left' }}>
                            <Typography variant="h4">
                              {formatDateRange(from, to)}
                              {startTime && endTime
                                ? `, ${startTime} - ${endTime}`
                                : ''}
                            </Typography>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </>
            )}
          </Stack>
        ),
        slotProps: {
          confirmButton: { label: 'OK' },
          cancelButton: { label: 'Cancel' },
        },
      });
      if (action === NOTIFICATION_DIALOG_ACTIONS.CONFIRM) {
        return book(payload);
      }
      return undefined;
    }

    if (!selectedSlot?.selectedIds.length && !isTypeChanged) {
      return book(payload);
    }

    if (isDayView) {
      // Retyping or reshaping the absence being edited is one hourly row
      // being rewritten, and the server answers for it -- writing the change
      // over it where somebody has approved the hours, replacing it where
      // nobody has. Only a selection taking in absences other than that one
      // is beyond what the day view can ask for.
      const others = (selectedSlot?.selectedIds ?? []).filter(
        (id) => !editedAbsenceIds.includes(id),
      );
      if (!others.length) {
        return book(payload);
      }
      setServerError(
        'Selected time overlaps an existing hourly absence for this user.',
      );
      return undefined;
    }

    const overrideIds =
      isTypeChanged && editedAbsenceIds.length > 0
        ? [
            ...new Set([
              ...(selectedSlot?.selectedIds ?? []),
              ...editedAbsenceIds,
            ]),
          ]
        : (selectedSlot?.selectedIds ?? []);

    const start = selectedSlot?.start;
    const end = selectedSlot?.end;
    if (start && end) {
      // Days somebody approved are not overwritten -- the change is proposed
      // over them and goes to whoever approved them -- so only the days that
      // really are about to be replaced are worth confirming.
      const overwrittenIds = overrideIds.filter((id) => {
        const absence = absences.find((item) => item.id === id);
        return (
          !!absence &&
          !requiresChangeRequest(
            absence,
            absenceTypes.find((type) => type.id === absence.typeId),
          )
        );
      });
      if (!overwrittenIds.length) {
        return book(payload);
      }

      const overrideDetails = getOverrideAbsenceDetails(
        overwrittenIds,
        absences,
        absenceTypes,
      );
      const { action } = await alert.open({
        title: 'Warning',
        children: (
          <Stack spacing={1} alignItems="center">
            <Typography variant="body1">
              You are about to override existing absence events:
            </Typography>
            <table>
              <tbody>
                {overrideDetails.map(
                  ({ key, typeName, from, to, startTime, endTime }) => (
                    <tr key={key}>
                      <td style={{ textAlign: 'left', paddingRight: 16 }}>
                        <Typography variant="h4">{typeName}:</Typography>
                      </td>
                      <td style={{ textAlign: 'left' }}>
                        <Typography variant="h4">
                          {formatDateRange(from, to)}
                          {startTime && endTime
                            ? `, ${startTime} - ${endTime}`
                            : ''}
                        </Typography>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </Stack>
        ),
        slotProps: {
          confirmButton: { label: 'OK' },
          cancelButton: { label: 'Cancel' },
        },
      });
      if (action === NOTIFICATION_DIALOG_ACTIONS.CONFIRM) {
        return book(payload);
      }
      return undefined;
    }

    return book(payload);
  };

  return (
    <Form
      saveShortcut={isFormEditable && !withdrawScope}
      schema={schema}
      defaultValues={getDefaultValues(selectedSlotData)}
      onError={(error, info) => {
        if (!info.isInvalid) {
          setServerError(getServerErrorMessage(error));
        }
      }}
      // A rejection the server words against a field this form does not have
      // -- `acti_date`, `acti_start_time` -- has nowhere to land, and used to
      // be dropped on the floor: the request failed and the user was told
      // nothing at all. Anything that cannot be pinned to an input is shown
      // in the header instead.
      onNonFieldError={(messages) => setServerError(messages.join(' '))}
      onSubmit={(values) => onSubmit(values)}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {(lockMessage || serverError) && (
        <FormHeader
          tone={lockMessage ? 'warning' : 'error'}
          message={(lockMessage || serverError) as string}
        />
      )}
      <FormFieldset
        disabled={!isFormEditable}
        disableUserField={absenceLock.status === ABSENCE_LOCK_STATUS.ACQUIRING}
        withdrawScope={withdrawScope}
      />
      <ScheduleFormActions
        disabled={!isFormEditable}
        withdrawing={!!withdrawScope}
      />
    </Form>
  );
}
